/**
 * Peças visuais compartilhadas pelas abas — espelham o painel HTML validado por ela em 18/09/2026
 * (layout lido do Mercado Turbo: célula empilhada preço + M.C., cards de campanha, botões
 * Participar/SAIR com confirmação em dois cliques).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Box, Button, Chip, Stack, Table, TableBody, TableCell, TableRow, Tooltip, Typography, useTheme } from '@mui/material'
import {
  classeMc, faltasTxt, mcCheioDe, mcDe, mcHoje, memoriaCalculo, paramsParaRemover, promoRange, selKey, TIPOS_SEM_POST, MOTIVO_SEM_POST,
  type Anuncio, type ClasseMc, type MemoriaCalculo, type Promocao, type ResultadoMC,
} from './logic'
import { usePromo } from './ctx'

// ---- formatação ---------------------------------------------------------------------------
export const brl = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
/** Sem o "R$": na 2ª linha da célula empilhada o preço logo acima já disse que é em reais. */
export const brlN = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
export const pct = (n: number | null | undefined) =>
  n == null ? '—' : `${n.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
export const ddmm = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : null)
export const dias = (a: string | null, b: string | null) =>
  a && b ? Math.max(1, Math.round((new Date(b).getTime() - new Date(a).getTime()) / 86400000)) : null
export const quando = (s: string | null | undefined) => String(s ?? '').replace('T', ' ').slice(0, 19)

/** Roxo de marca do Mercado Turbo (card de campanha selecionado) — medido em 29/08/2026. */
export const BRAND = '#614785'
/** Laranja do "dado incompleto": nunca o mesmo tom do vermelho de margem ruim. */
export const LARANJA = '#c2410c'

export function useCorMc() {
  const t = useTheme()
  return (c: ClasseMc): string | undefined => {
    switch (c) {
      case 'pos': return t.palette.success.main
      case 'mid': return t.palette.warning.main
      case 'neg': return t.palette.error.main
      case 'inc': return LARANJA
      default: return undefined
    }
  }
}

export const mono = { fontFamily: '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace' } as const
export const num = { fontVariantNumeric: 'tabular-nums' } as const

// ---- pequenos ----------------------------------------------------------------------------
export function IncMark({ faltas }: { faltas: string[] }) {
  if (!faltas.length) return null
  return (
    <Tooltip title={faltasTxt(faltas)} arrow>
      <Box component="span" sx={{ color: LARANJA, fontSize: 11, cursor: 'help', ml: 0.4 }}>⚠</Box>
    </Tooltip>
  )
}

export function Pill({ label, tone, dashed }: { label: string; tone?: 'on' | 'cand' | 'mock' | 'none'; dashed?: boolean }) {
  const t = useTheme()
  const cor = tone === 'on' || tone === 'mock' ? t.palette.success.main : tone === 'cand' ? t.palette.primary.main : t.palette.text.secondary
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block', fontSize: 10, px: 0.9, py: 0.1, borderRadius: 99, whiteSpace: 'nowrap',
        border: `1px ${dashed || tone === 'mock' ? 'dashed' : 'solid'} ${tone && tone !== 'none' ? cor : t.palette.divider}`,
        color: cor, lineHeight: 1.6,
      }}
    >
      {label}
    </Box>
  )
}

export function Panel({ title, tone, children, count }: { title?: ReactNode; tone?: 'alert' | 'critico' | 'good'; children: ReactNode; count?: ReactNode }) {
  const t = useTheme()
  const borda = tone === 'critico' ? t.palette.error.main : tone === 'alert' ? t.palette.warning.main : tone === 'good' ? t.palette.success.main : t.palette.divider
  return (
    <Box sx={{ border: `1px solid ${borda}`, borderRadius: 3, p: { xs: 1.5, sm: 2.25 }, bgcolor: 'background.paper' }}>
      {title ? (
        <Typography component="div" sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: 15, mb: 1, color: tone === 'critico' ? 'error.main' : tone === 'alert' ? 'warning.main' : undefined }}>
          {title}{count != null ? <Typography component="span" sx={{ fontSize: 12, fontWeight: 500, color: 'text.secondary', ml: 1 }}>{count}</Typography> : null}
        </Typography>
      ) : null}
      {children}
    </Box>
  )
}

export function P({ children }: { children: ReactNode }) {
  return <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25, maxWidth: '92ch', lineHeight: 1.6 }}>{children}</Typography>
}

/** Selo do cabeçalho "Ação": diz se o botão escreve de verdade ou é só preview. */
export function SeloAcao() {
  const { escreve, live } = usePromo()
  const t = useTheme()
  const teste = escreve && live?.dry_run
  return (
    <Box component="span" sx={{
      fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '.05em', borderRadius: 99, px: 0.9, ml: 0.75,
      border: `1px ${escreve ? 'solid' : 'dashed'} ${escreve ? (teste ? t.palette.warning.main : t.palette.error.main) : t.palette.text.disabled}`,
      color: escreve ? (teste ? t.palette.warning.main : t.palette.error.main) : t.palette.text.disabled, verticalAlign: 'middle',
    }}>
      {escreve ? (teste ? 'teste' : 'escreve na API') : 'preview'}
    </Box>
  )
}

// ---- memória de cálculo (o balão ao passar o mouse na M.C.) ----------------------------------
export function MemoriaBox({ mem }: { mem: MemoriaCalculo }) {
  const cor = useCorMc()
  const { alvo } = usePromo()
  return (
    <Box sx={{ fontSize: 12, minWidth: 300, maxWidth: 420 }}>
      <Typography sx={{ fontWeight: 700, mb: 0.75, fontSize: 12.5 }}>{mem.titulo}</Typography>
      <Table size="small">
        <TableBody>
          {mem.parcelas.map((p) => (
            <TableRow key={p.rotulo}>
              <TableCell sx={{ py: 0.3, border: 0, pl: 0, whiteSpace: 'nowrap', color: 'text.secondary' }}>{p.sinal ? `${p.sinal} ` : ''}{p.rotulo}</TableCell>
              <TableCell align="right" sx={{ py: 0.3, border: 0, ...num, whiteSpace: 'nowrap' }}>
                {p.valor == null ? <Box component="span" sx={{ color: LARANJA, fontSize: 11 }}>não entrou na conta</Box> : brl(Math.abs(p.valor))}
              </TableCell>
              <TableCell sx={{ py: 0.3, border: 0, color: 'text.secondary', fontSize: 11, lineHeight: 1.25 }}>{p.origem}</TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell sx={{ py: 0.6, pl: 0, fontWeight: 700, borderBottom: 0, borderTop: 1, borderColor: 'divider' }}>= M.C.</TableCell>
            <TableCell align="right" sx={{ py: 0.6, fontWeight: 700, borderBottom: 0, borderTop: 1, borderColor: 'divider', color: cor(classeMc(mem.total, alvo)), ...num }}>
              {mem.total.rs == null ? '—' : brl(mem.total.rs)}
            </TableCell>
            <TableCell sx={{ py: 0.6, borderBottom: 0, borderTop: 1, borderColor: 'divider', color: 'text.secondary', fontSize: 11 }}>
              {mem.total.pct == null ? '' : `${pct(mem.total.pct)} do preço`}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {mem.total.faltas.length ? (
        <Typography sx={{ mt: 0.75, fontSize: 11, color: LARANJA, lineHeight: 1.35 }}>⚠ Número otimista: <b>{mem.total.faltas.join(', ')}</b> não entrou na conta.</Typography>
      ) : null}
      <Typography sx={{ mt: 0.75, pt: 0.6, borderTop: 1, borderColor: 'divider', fontSize: 11, color: 'text.secondary' }}>{mem.rodape} — troque no botão do topo.</Typography>
    </Box>
  )
}

export function McTip({ a, p, children }: { a: Anuncio; p: Promocao | null; children: ReactNode }) {
  const { base, cmvManual } = usePromo()
  return (
    <Tooltip title={<MemoriaBox mem={memoriaCalculo(a, p, base, cmvManual)} />} arrow placement="left" enterDelay={250}
      slotProps={{ tooltip: { sx: { maxWidth: 460, bgcolor: 'background.paper', color: 'text.primary', border: 1, borderColor: 'divider', boxShadow: 6 } } }}>
      <Box component="span" sx={{ cursor: 'help', display: 'inline-block' }}>{children}</Box>
    </Tooltip>
  )
}

// ---- células empilhadas (estilo Mercado Turbo) --------------------------------------------
export function Stack2({ top, bottom, classe, strong }: { top: ReactNode; bottom?: ReactNode; classe?: ClasseMc; strong?: boolean }) {
  const cor = useCorMc()
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.1, ...num }}>
      <Box component="span" sx={{ fontWeight: 600, fontSize: 13.5, color: strong && classe ? cor(classe) : undefined, whiteSpace: 'nowrap' }}>{top}</Box>
      {bottom != null ? <Box component="span" sx={{ fontSize: 11.5, color: classe ? cor(classe) : 'text.secondary', whiteSpace: 'nowrap' }}>{bottom}</Box> : null}
    </Box>
  )
}

/** Preço cheio + "MC 18,00 · 23%" + "hoje …" (margem da promoção ativa, com o abate do ML). */
export function OrigCell({ a }: { a: Anuncio }) {
  const { base, alvo, cmvManual } = usePromo()
  const cor = useCorMc()
  const mc = mcCheioDe(a, base)
  const hoje = mcHoje(a, base, cmvManual)
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.1, ...num }}>
      <Box component="span" sx={{ fontWeight: 600, fontSize: 13.5 }}>{brl(a.preco_atual)}</Box>
      <McTip a={a} p={null}>
        {mc.pct == null
          ? <Box component="span" sx={{ fontSize: 11, color: 'text.disabled' }}>{faltasTxt(mc.faltas) || 'sem MC'}</Box>
          : <Box component="span" sx={{ fontSize: 11.5, color: cor(classeMc(mc, alvo)) }}>MC {brlN(mc.rs)} · {pct(mc.pct)}<IncMark faltas={mc.faltas} /></Box>}
      </McTip>
      {hoje ? (
        <McTip a={a} p={hoje.p}>
          <Tooltip title={`${hoje.p.name || hoje.p.type_label} · ${brl(hoje.p.preco_promo)}${hoje.p.rebate_ml_rs ? ` · ML abate ${brl(hoje.p.rebate_ml_rs)} da taxa` : ''}`}>
            <Box component="span" sx={{ fontSize: 11.5, color: hoje.m.rs == null ? 'text.disabled' : cor(classeMc(hoje.m, alvo)) }}>
              {hoje.m.rs == null ? `hoje: ${faltasTxt(hoje.m.faltas) || 'sem MC'}` : <>hoje {brlN(hoje.m.rs)} · {pct(hoje.m.pct)}<IncMark faltas={hoje.m.faltas} />{hoje.p.rebate_ml_rs ? <Box component="span" sx={{ color: 'text.disabled' }}> ↺ML</Box> : null}</>}
            </Box>
          </Tooltip>
        </McTip>
      ) : null}
    </Box>
  )
}

/** M.C. final — quanto sobra DEPOIS do desconto, no preço da promoção. É a coluna que decide. */
export function McFinalCell({ a, p }: { a: Anuncio; p: Promocao | null }) {
  const { base, alvo, cmvManual } = usePromo()
  if (!p) return <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
  const m = mcDe(a, p, base, cmvManual)
  if (m.rs == null) return <McTip a={a} p={p}><Box component="span" sx={{ fontSize: 11, color: 'text.disabled' }}>{faltasTxt(m.faltas) || 'sem simulação'}</Box></McTip>
  const c = classeMc(m, alvo)
  return (
    <McTip a={a} p={p}>
      <Stack2 top={brl(m.rs)} bottom={<>{pct(m.pct)}<IncMark faltas={m.faltas} /></>} classe={c} strong />
    </McTip>
  )
}

export function PrecoPromoCell({ p }: { p: Promocao | null }) {
  if (!p) return <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
  if (p.preco_promo == null) return <Box component="span" sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: 12.5 }}>sem preço p/ simular</Box>
  const off = p.original_price ? Math.round((1 - p.preco_promo / p.original_price) * 1000) / 10 : null
  return <Stack2 top={brl(p.preco_promo)} bottom={off != null ? `−${off}%` : undefined} />
}

/** "Eu banco / ML banca / Desconto total", com a origem do split sempre dita. */
export function SplitCell({ rs, pc, p }: { rs: number | null; pc: number | null; p: Promocao }) {
  const semDado = p.split_origem === 'não informado pela API'
  const confirmado = (p.split_origem || '').startsWith('confirmado')
  if (rs == null) return <Box component="span" sx={{ color: 'text.disabled', fontSize: 11 }}>{semDado ? 'não informado' : '—'}</Box>
  return (
    <Tooltip title={p.split_origem || ''}>
      <Box><Stack2 top={brl(rs)} bottom={<>{pct(pc)}{confirmado ? <Box component="span" sx={{ color: 'text.disabled' }}> · confirmado</Box> : null}</>} /></Box>
    </Tooltip>
  )
}

/** Miniatura + título + MLB + SKU + estoque. */
export function AdCell({ a, caret, extra, size = 38 }: { a: Anuncio; caret?: boolean; extra?: ReactNode; size?: number }) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
      {caret ? <Box component="span" sx={{ width: 14, color: 'text.disabled', fontSize: 11 }} title="Ver todas as promoções deste anúncio">↗</Box> : null}
      {a.thumbnail
        ? <Box component="img" src={a.thumbnail.replace('http://', 'https://')} alt="" loading="lazy" sx={{ width: size, height: size, borderRadius: 2, objectFit: 'cover', border: 1, borderColor: 'divider', flex: 'none', bgcolor: 'action.hover' }} />
        : <Box sx={{ width: size, height: size, borderRadius: 2, border: 1, borderColor: 'divider', flex: 'none', bgcolor: 'action.hover' }} />}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" noWrap title={a.titulo} sx={{ maxWidth: 260, fontSize: 13 }}>{a.titulo}</Typography>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.2 }}>
          <Box component="span" sx={{ ...mono, fontSize: 11.5, color: 'text.disabled' }}>{a.mlb}</Box>
          {a.sku ? <Pill label={a.sku} /> : null}
          <Box component="span" sx={{ fontSize: 10.5, color: 'text.disabled' }}>estoque {a.estoque}</Box>
          {extra}
        </Stack>
      </Box>
    </Stack>
  )
}

// ---- botões de ação: dois cliques quando a escrita é real ----------------------------------
/** Arma por `ms` e desarma sozinho: clique perdido não fica esperando um segundo clique. */
export function useArmado(ms = 5000): [boolean, () => void, () => void] {
  const [armado, setArmado] = useState(false)
  const timer = useRef<number | null>(null)
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current) }, [])
  const armar = () => {
    setArmado(true)
    if (timer.current) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setArmado(false), ms)
  }
  const desarmar = () => { setArmado(false); if (timer.current) window.clearTimeout(timer.current) }
  return [armado, armar, desarmar]
}

type EstadoBtn = { txt: string; tone: 'ok' | 'erro' | 'info'; title?: string } | null

/**
 * Participar. Com faixa → abre o modal (escolha do valor). Sem faixa → aceite direto:
 * em preview marca na tela; com o serviço no ar pede confirmação no próprio botão e escreve.
 */
export function BotaoParticipar({ a, p, icone }: { a: Anuncio; p: Promocao; icone?: boolean }) {
  const ctx = usePromo()
  const [armado, armar, desarmar] = useArmado(4000)
  const [busy, setBusy] = useState(false)
  const [estado, setEstado] = useState<EstadoBtn>(null)
  const k = selKey(a, p)
  const range = promoRange(p)

  const clicar = async () => {
    if (range) { ctx.abrirModal(a, p); return }
    if (p.preco_promo == null) { setEstado({ txt: 'sem valor na API', tone: 'erro', title: 'Esta campanha não devolveu preço nem faixa — não dá para aceitar por aqui.' }); return }
    if (!ctx.escreve) { ctx.marcar(k, 'started', true); ctx.toggleSel(k, true); return }
    if (!armado) { armar(); return }
    desarmar(); setBusy(true)
    const r = await ctx.ativar(a, p, p.preco_promo)
    setBusy(false)
    setEstado(r.ok
      ? { txt: r.simulado ? '✓ simulado' : '✓ ativada', tone: 'ok', title: `HTTP ${r.http} — registrado em escritas_promo.log` }
      : { txt: 'ML recusou', tone: 'erro', title: `HTTP ${r.http ?? 0}: ${r.erro ?? JSON.stringify(r.resposta ?? '')}` })
  }

  // 21/10/2026 (ela: "relâmpago smart é diferente da relâmpago normal"): o corpo do POST deste tipo não está
  // documentado — não se manda POST às cegas. Fica visível, travado, até validarmos em 1 anúncio com o OK dela.
  if (TIPOS_SEM_POST.has(p.type)) {
    return (
      <Tooltip title="O corpo do POST deste tipo não está na documentação do ML. Não mandamos POST às cegas: a regra entra depois de um teste controlado em um anúncio, com o seu OK.">
        <span>
          <Button size="small" variant="outlined" color="inherit" disabled sx={{ whiteSpace: 'nowrap', fontSize: 12, py: 0.4, px: 1.5, minWidth: 0 }}>
            {icone ? '⚡?' : MOTIVO_SEM_POST}
          </Button>
        </span>
      </Tooltip>
    )
  }
  const off = p.original_price && p.preco_promo != null ? Math.round((1 - p.preco_promo / p.original_price) * 1000) / 10 : null
  const label = busy ? 'enviando…' : estado ? estado.txt : armado ? 'Confirmar aceite?' : icone ? '⚙' : 'Participar'
  const title = estado?.title ?? (armado ? `${a.mlb} passa a ${brl(p.preco_promo)}${off != null ? ` (−${off}%)` : ''} no Mercado Livre, de verdade. Clique de novo para confirmar.` : icone ? 'Escolher o valor e participar' : undefined)
  return (
    <Tooltip title={title ?? ''}>
      <span>
        <Button size="small" variant={armado ? 'contained' : 'outlined'} color={estado?.tone === 'erro' ? 'error' : armado ? 'error' : 'success'}
          disabled={busy || estado?.tone === 'erro' && estado.txt === 'sem valor na API'} onClick={() => void clicar()}
          sx={{ whiteSpace: 'nowrap', fontSize: 12, py: 0.4, px: icone ? 1 : 1.5, minWidth: 0, borderStyle: ctx.escreve ? 'solid' : 'dashed', fontWeight: armado ? 700 : 500 }}>
          {label}
        </Button>
      </span>
    </Tooltip>
  )
}

/** SAIR: DELETE de verdade, em dois cliques. Em preview só desfaz a marcação da própria sessão. */
export function BotaoSair({ a, p }: { a: Anuncio; p: Promocao }) {
  const ctx = usePromo()
  const [armado, armar, desarmar] = useArmado(8000)
  const [busy, setBusy] = useState(false)
  const [estado, setEstado] = useState<EstadoBtn>(null)
  const k = selKey(a, p)
  const local = ctx.statusLocal.get(k)

  const clicar = async () => {
    if (!ctx.escreve) {
      if (local?.preview) { ctx.desmarcar(k); return }
      setEstado({ txt: 'preview — nada enviado', tone: 'info', title: 'A tela está sem o serviço de escrita, então nenhuma chamada é feita à API do Mercado Livre. Esta promoção continua ativa no ML.' })
      window.setTimeout(() => setEstado(null), 3500)
      return
    }
    if (!armado) { armar(); return }
    desarmar(); setBusy(true)
    const r = await ctx.remover(a, p)
    setBusy(false)
    setEstado(r.ok
      ? { txt: r.simulado ? '✓ simulado' : r.ja_fora ? '✓ já estava fora' : '✓ saiu', tone: 'ok', title: r.ja_fora ? 'A promoção já não estava ativa no ML — nada foi enviado. Some do painel na próxima coleta.' : `Removida — HTTP ${r.http}. Registrado em escritas_promo.log.` }
      : { txt: r.relampago ? '✗ relâmpago não sai' : '✗ o ML recusou', tone: 'erro', title: r.relampago ? 'Oferta relâmpago não pode ser removida pelo vendedor (regra do ML) — acaba sozinha no fim da oferta.' : `HTTP ${r.http ?? 0}: ${r.erro ?? JSON.stringify(r.resposta ?? '')} — nada mudou no anúncio.` })
    if (!r.ok) window.setTimeout(() => setEstado(null), 6000)
  }
  const title = estado?.title ?? (armado
    ? `Vai ser enviado de verdade: DELETE /seller-promotions/items/${a.mlb}?promotion_type=${p.type}${paramsParaRemover(p).promotion_id ? `&promotion_id=${paramsParaRemover(p).promotion_id}` : ''}${paramsParaRemover(p).offer_id ? `&offer_id=${paramsParaRemover(p).offer_id}` : ''} — o anúncio sai desta promoção e volta ao preço cheio (${brl(a.preco_atual)}). Clique de novo para confirmar.`
    : 'Sair desta promoção')
  return (
    <Tooltip title={title}>
      <span>
        <Button size="small" variant={armado ? 'outlined' : 'contained'} color="error" disabled={busy || estado?.tone === 'ok'} onClick={() => void clicar()}
          sx={{ whiteSpace: 'nowrap', fontSize: 12, py: 0.4, px: 1.5, fontWeight: 700, borderStyle: armado ? 'dashed' : 'solid' }}>
          {busy ? 'saindo…' : estado ? estado.txt : armado ? '⚠ Confirmar saída' : 'SAIR'}
        </Button>
      </span>
    </Tooltip>
  )
}

/** Coluna Ação: já ativa → SAIR; senão → Participar (⚙ quando tem faixa, na aba Por anúncio). */
export function AcaoCell({ a, p, icone }: { a: Anuncio; p: Promocao | null; icone?: boolean }) {
  if (!p) return <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
  return p.status === 'started' ? <BotaoSair a={a} p={p} /> : <BotaoParticipar a={a} p={p} icone={icone && !!promoRange(p)} />
}

export function StatusPill({ p, k }: { p: Promocao; k: string }) {
  const { statusLocal } = usePromo()
  const local = statusLocal.get(k)
  if (local?.preview && local.status === 'started') return <Pill label="Ativa (preview)" tone="mock" />
  return <Pill label={p.status_label} tone={p.status === 'started' ? 'on' : p.status === 'candidate' ? 'cand' : 'none'} />
}

export function ChipCount({ n, label }: { n: number; label: string }) {
  return <Chip size="small" label={`${n.toLocaleString('pt-BR')} ${label}`} variant="outlined" sx={{ height: 20, fontSize: 11 }} />
}

export const mcOuTraco = (m: ResultadoMC) => (m.rs == null ? '—' : `${brl(m.rs)} · ${pct(m.pct)}`)
